import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.primary ? '#007bff' : props.success ? '#28a745' : props.danger ? '#dc3545' : '#6c757d'};
  color: #fff;

  &:disabled {
    background: #c0c4cc;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #007bff;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem;
  color: #333;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 220px;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid #e9ecef;
  color: #495057;
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  color: #333;
  vertical-align: middle;
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  background: ${props => props.$high ? '#e6f7ff' : '#f1f3f5'};
  color: ${props => props.$high ? '#1890ff' : '#6c757d'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  width: ${props => props.$wide ? '760px' : '460px'};
  max-width: 92%;
  max-height: 86vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.125rem;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  box-sizing: border-box;
  resize: vertical;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const Note = styled.p`
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.6;
  margin: 0.5rem 0 0;
`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const ACTION_TEXT = {
  daily_login: '每日登录奖励',
  purchase: '消费获得',
  exchange: '积分兑换',
  admin_adjust: '管理员调整',
  batch_reward: '批量奖励',
  register: '注册赠送'
};

const PointsPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState('points');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedIds, setSelectedIds] = useState([]);

  const [adjustTarget, setAdjustTarget] = useState(null); // { _id, username }
  const [adjustForm, setAdjustForm] = useState({ type: 'add', amount: '', reason: '' });
  const [adjusting, setAdjusting] = useState(false);

  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({ amount: '', reason: '' });
  const [batching, setBatching] = useState(false);

  const [recordsUser, setRecordsUser] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const notify = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => { setMessage(''); setMessageType(''); }, 3000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/points/admin/stats', authHeaders());
      if (res.data.success) setStats(res.data.data);
    } catch (error) {
      console.error('获取积分统计失败:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/points/admin/list', {
        ...authHeaders(),
        params: { page, limit, search: keyword, sort }
      });
      if (res.data.success) {
        setUsers(res.data.data.users || []);
        setTotal(res.data.data.pagination.total || 0);
        setTotalPages(res.data.data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('获取用户积分列表失败:', error);
      notify('获取用户积分列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, keyword, sort]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(1);
    setKeyword(search.trim());
  };

  const openAdjust = (user) => {
    setAdjustTarget(user);
    setAdjustForm({ type: 'add', amount: '', reason: '' });
  };

  const submitAdjust = async () => {
    const amount = Number(adjustForm.amount);
    if (!Number.isFinite(amount) || amount === 0) {
      notify('请输入有效的积分数量', 'error');
      return;
    }
    if (!adjustForm.reason.trim()) {
      notify('请输入调整原因', 'error');
      return;
    }

    setAdjusting(true);
    try {
      const signed = adjustForm.type === 'add' ? Math.abs(amount) : -Math.abs(amount);
      const res = await axios.post(
        `/api/admin/points/adjust/${adjustTarget._id}`,
        { points: signed, reason: adjustForm.reason.trim() },
        authHeaders()
      );
      if (res.data.success) {
        notify(res.data.message || '积分调整成功');
        setAdjustTarget(null);
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      notify(error.response?.data?.message || '积分调整失败', 'error');
    } finally {
      setAdjusting(false);
    }
  };

  const submitBatch = async () => {
    const amount = Number(batchForm.amount);
    if (!selectedIds.length) {
      notify('请先选择用户', 'error');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      notify('请输入有效的积分数量', 'error');
      return;
    }
    if (!batchForm.reason.trim()) {
      notify('请输入添加原因', 'error');
      return;
    }

    setBatching(true);
    try {
      const res = await axios.post(
        '/api/admin/points/admin/batch-add',
        { userIds: selectedIds, points: amount, reason: batchForm.reason.trim() },
        authHeaders()
      );
      if (res.data.success) {
        notify(res.data.message || '批量添加成功');
        setBatchOpen(false);
        setSelectedIds([]);
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      notify(error.response?.data?.message || '批量添加失败', 'error');
    } finally {
      setBatching(false);
    }
  };

  const openRecords = async (user) => {
    setRecordsUser(user);
    setRecordsLoading(true);
    try {
      const res = await axios.get(`/api/admin/points/admin/records/${user._id}`, {
        ...authHeaders(),
        params: { page: 1, limit: 50 }
      });
      if (res.data.success) setRecords(res.data.data.records || []);
    } catch (error) {
      notify('获取积分记录失败', 'error');
    } finally {
      setRecordsLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === users.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(users.map(u => u._id));
    }
  };

  return (
    <AdminLayout title="积分管理">
    <div>
      <PageHeader>
        <PageTitle>积分管理</PageTitle>
        <HeaderActions>
          <Button onClick={() => { fetchStats(); fetchUsers(); }}>刷新</Button>
          <Button
            success
            disabled={selectedIds.length === 0}
            onClick={() => { setBatchForm({ amount: '', reason: '' }); setBatchOpen(true); }}
          >
            批量加分{selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}
          </Button>
        </HeaderActions>
      </PageHeader>

      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 6,
          marginBottom: '1rem',
          background: messageType === 'error' ? '#fdecea' : '#e8f5e9',
          color: messageType === 'error' ? '#c62828' : '#2e7d32',
          fontSize: '0.875rem'
        }}>
          {message}
        </div>
      )}

      {/* 统计概览 */}
      <StatsGrid>
        <StatCard>
          <StatValue>{(stats?.totalUsers ?? 0).toLocaleString()}</StatValue>
          <StatLabel>用户总数</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{(stats?.currentPoints ?? 0).toLocaleString()}</StatValue>
          <StatLabel>当前积分总量</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{(stats?.totalEarned ?? 0).toLocaleString()}</StatValue>
          <StatLabel>累计发放积分</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{(stats?.totalSpent ?? 0).toLocaleString()}</StatValue>
          <StatLabel>累计消耗积分</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{(stats?.avgPoints ?? 0).toLocaleString()}</StatValue>
          <StatLabel>人均持有积分</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* 等级分布 */}
      {stats && (stats.levelDistribution || []).length > 0 && (
        <Card>
          <CardTitle>等级分布</CardTitle>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {stats.levelDistribution.map(item => (
              <LevelBadge key={item.level} $high={item.level >= 5}>
                Lv.{item.level} · {item.count} 人
              </LevelBadge>
            ))}
          </div>
        </Card>
      )}

      {/* 用户积分列表 */}
      <Card>
        <CardTitle>用户积分（共 {total} 位）</CardTitle>

        <FilterBar>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="用户名 / 手机号 / 邮箱"
          />
          <Button onClick={handleSearch}>搜索</Button>
          <FilterSelect value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            <option value="points">按积分高低</option>
            <option value="level">按等级高低</option>
            <option value="newest">最新注册</option>
            <option value="oldest">最早注册</option>
          </FilterSelect>
        </FilterBar>

        {loading ? (
          <EmptyState>加载中...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>暂无用户数据</EmptyState>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === users.length}
                      onChange={toggleSelectAll}
                    />
                  </Th>
                  <Th>用户</Th>
                  <Th>等级</Th>
                  <Th>当前积分</Th>
                  <Th>累计获得</Th>
                  <Th>累计消耗</Th>
                  <Th>注册时间</Th>
                  <Th>操作</Th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(u._id)}
                        onChange={() => toggleSelect(u._id)}
                      />
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{u.nickname || u.username}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {u.phone || u.email || ''}
                      </div>
                    </Td>
                    <Td>
                      <LevelBadge $high={(u.level?.current || 1) >= 5}>
                        Lv.{u.level?.current || 1} {u.level?.title || ''}
                      </LevelBadge>
                    </Td>
                    <Td style={{ fontWeight: 600 }}>{(u.points?.current || 0).toLocaleString()}</Td>
                    <Td>{(u.points?.totalEarned || 0).toLocaleString()}</Td>
                    <Td>{(u.points?.totalSpent || 0).toLocaleString()}</Td>
                    <Td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button onClick={() => openAdjust(u)}>调整</Button>
                        <Button onClick={() => openRecords(u)}>记录</Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
            <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>第 {page} / {totalPages} 页</span>
            <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>下一页</Button>
          </Pagination>
        )}
      </Card>

      {/* 调整积分 */}
      {adjustTarget && (
        <ModalOverlay onClick={() => setAdjustTarget(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>调整积分 · {adjustTarget.nickname || adjustTarget.username}</ModalTitle>
            <FormGroup>
              <Label>调整方式</Label>
              <FilterSelect
                value={adjustForm.type}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, type: e.target.value }))}
                style={{ width: '100%' }}
              >
                <option value="add">增加积分</option>
                <option value="deduct">扣除积分</option>
              </FilterSelect>
            </FormGroup>
            <FormGroup>
              <Label>积分数量</Label>
              <Input
                type="number"
                min="1"
                value={adjustForm.amount}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="请输入正整数"
              />
            </FormGroup>
            <FormGroup>
              <Label>调整原因（必填，会记入用户积分记录）</Label>
              <TextArea
                rows={3}
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="如：活动奖励 / 补偿扣除"
              />
            </FormGroup>
            <Note>扣除不会让积分变为负：余额不足时最多扣至 0。</Note>
            <ModalActions>
              <Button onClick={() => setAdjustTarget(null)}>取消</Button>
              <Button primary disabled={adjusting} onClick={submitAdjust}>
                {adjusting ? '提交中...' : '确认调整'}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 批量加分 */}
      {batchOpen && (
        <ModalOverlay onClick={() => setBatchOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>批量加分 · 已选 {selectedIds.length} 位用户</ModalTitle>
            <FormGroup>
              <Label>积分数量</Label>
              <Input
                type="number"
                min="1"
                value={batchForm.amount}
                onChange={(e) => setBatchForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="请输入正整数"
              />
            </FormGroup>
            <FormGroup>
              <Label>添加原因（必填）</Label>
              <TextArea
                rows={3}
                value={batchForm.reason}
                onChange={(e) => setBatchForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="如：节日活动奖励"
              />
            </FormGroup>
            <ModalActions>
              <Button onClick={() => setBatchOpen(false)}>取消</Button>
              <Button success disabled={batching} onClick={submitBatch}>
                {batching ? '提交中...' : '确认添加'}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 积分记录 */}
      {recordsUser && (
        <ModalOverlay onClick={() => setRecordsUser(null)}>
          <ModalContent $wide onClick={(e) => e.stopPropagation()}>
            <ModalTitle>积分记录 · {recordsUser.nickname || recordsUser.username}</ModalTitle>
            {recordsLoading ? (
              <EmptyState>加载中...</EmptyState>
            ) : records.length === 0 ? (
              <EmptyState>暂无积分记录</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>时间</Th>
                    <Th>类型</Th>
                    <Th>说明</Th>
                    <Th>变动</Th>
                    <Th>余额</Th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <Td>{new Date(r.createdAt).toLocaleString('zh-CN')}</Td>
                      <Td>{ACTION_TEXT[r.action] || r.action}</Td>
                      <Td>{r.description || '-'}</Td>
                      <Td style={{
                        fontWeight: 600,
                        color: r.type === 'earn' ? '#2e7d32' : r.type === 'spend' ? '#c62828' : '#495057'
                      }}>
                        {r.type === 'earn' ? `+${r.points}` : r.type === 'spend' ? `-${r.points}` : r.points}
                      </Td>
                      <Td>{r.balance}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <ModalActions>
              <Button onClick={() => setRecordsUser(null)}>关闭</Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
    </AdminLayout>
  );
};

export default PointsPage;
